import { useState } from "react";
import { runInAction } from "mobx";

import apiService from "../../services/apiService";
import { activeMeterNumber, setActiveMeterNumber } from "../../utils/stationMeter";

/**
 * `(8)` (`АРХ-ФРОНТ-0311`): правка заводского номера действующего счётчика в
 * окне станции — только root, в режиме правки. Отдельный вызов
 * `PATCH /station/:id/meter/number`, отдельно от сохранения станции.
 *
 * После 200 новый номер сразу пишется поверх станции в сторе страницы (это тот
 * же объект, что в списке на `/inplants` и на «Все станции»), затем страница
 * перечитывается. Перечитывание отказало — на экране всё равно новый номер.
 * Отказ сервера — текстом у поля; введённое остаётся, окно — в правке.
 */
export const METER_NUMBER_FALLBACK =
  "номер не изменён — причина не названа сервером";

export function meterNumberErrorText(error: any): string {
  const data = error?.response?.data;
  const first = Array.isArray(data?.errors) ? data.errors[0] : null;
  const fieldText = first?.message || first?.msg;

  if (error?.response?.status === 400 && fieldText) return String(fieldText);

  return data?.message || data?.error || fieldText || METER_NUMBER_FALLBACK;
}

export function useMeterNumberEdit(station: any, refreshData?: () => unknown) {
  const [draft, setDraft] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const current = activeMeterNumber(station) ?? "";
  const changed = draft !== null && draft.trim() !== current;

  const onChange = (value: string) => {
    setDraft(value);
    setError(null);
  };

  /** `true` — номер принят или не менялся; `false` — отказ, окно в правке. */
  const save = async (): Promise<boolean> => {
    if (!station?.id || !changed || draft === null) return true;

    const number = draft.trim();

    try {
      await apiService.PATCH(`/station/${station.id}/meter/number`, {
        meter_number: number,
      });
    } catch (failure) {
      setError(meterNumberErrorText(failure));
      return false;
    }

    // `АУД-Н350`: станция — observable стора; запись через `runInAction`,
    // иначе MobX (strict-mode) предупреждает в консоли на каждой правке.
    runInAction(() => setActiveMeterNumber(station, number));
    setDraft(null);
    setError(null);

    try {
      await refreshData?.();
    } catch {
      // Номер уже в сторе — перечитывание лишь сверяет его с сервером.
    }

    return true;
  };

  const reset = () => {
    setDraft(null);
    setError(null);
  };

  return { value: draft ?? current, error, changed, onChange, save, reset };
}
